/**
 * CorsOrigins - Admin page for managing allowed CORS origins
 * Controls which domains can embed the public booking widget
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Globe,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  ExternalLink,
  Copy,
  Check,
} from 'lucide-react';
import { api } from '../../lib/api';
import { pageStyles } from '../../lib/styles';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';

// ============================================================================
// Types
// ============================================================================

interface AllowedOrigin {
  id: string;
  origin: string;
  createdAt: string;
}

interface OriginsResponse {
  success: boolean;
  data: AllowedOrigin[];
}

// ============================================================================
// Main Component
// ============================================================================

export default function CorsOriginsPage() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newOrigin, setNewOrigin] = useState('');
  const [deleteOrigin, setDeleteOrigin] = useState<AllowedOrigin | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { success: showSuccess, error: showError } = useToast();
  const queryClient = useQueryClient();

  // Fetch all origins
  const { data: response, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'cors-origins'],
    queryFn: () => api.get<OriginsResponse>('/api/admin/cors-origins'),
  });

  const origins = response?.data ?? [];

  // Add origin mutation
  const addMutation = useMutation({
    mutationFn: (origin: string) =>
      api.post('/api/admin/cors-origins', { origin }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cors-origins'] });
      showSuccess('Origen añadido correctamente');
      setIsAddDialogOpen(false);
      setNewOrigin('');
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Error al añadir origen');
    },
  });

  // Delete origin mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/cors-origins/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cors-origins'] });
      showSuccess('Origen eliminado');
      setDeleteOrigin(null);
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Error al eliminar origen');
    },
  });

  const handleCopyOrigin = async (origin: AllowedOrigin) => {
    try {
      await navigator.clipboard.writeText(origin.origin);
      setCopiedId(origin.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      showError('Error al copiar');
    }
  };

  const handleAddOrigin = () => {
    if (!newOrigin.trim()) return;
    addMutation.mutate(newOrigin.trim());
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className={pageStyles.title}>Orígenes CORS</h1>
        </div>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Error al cargar orígenes</p>
                <p className="text-sm text-red-600 mt-1">
                  {error instanceof Error ? error.message : 'Error desconocido'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={pageStyles.title}>Orígenes CORS</h1>
          <p className={pageStyles.subtitle}>
            Dominios autorizados para incrustar el widget de reservas
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Añadir origen
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Globe className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">¿Qué son los orígenes CORS?</p>
              <p className="text-sm text-blue-700 mt-1">
                Los orígenes CORS permiten que sitios web externos carguen el widget de reservas
                de tu taller. Añade aquí los dominios donde quieras incrustar el widget
                (ej: https://tudominio.com).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Origins List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Orígenes permitidos
          </CardTitle>
          <CardDescription>
            {origins.length === 0
              ? 'No hay orígenes configurados'
              : `${origins.length} origen${origins.length !== 1 ? 'es' : ''} configurado${origins.length !== 1 ? 's' : ''}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {origins.length === 0 ? (
            <div className="text-center py-8">
              <Globe className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-lg">No hay orígenes configurados</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Añade un origen para permitir que sitios externos carguen el widget
              </p>
              <Button className="mt-4" onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Añadir primer origen
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {origins.map((origin) => (
                <div
                  key={origin.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm font-mono">{origin.origin}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => handleCopyOrigin(origin)}
                        >
                          {copiedId === origin.id ? (
                            <Check className="h-3 w-3 text-green-600" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                        <a
                          href={origin.origin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Añadido el {new Date(origin.createdAt).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setDeleteOrigin(origin)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Origin Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Añadir origen CORS</DialogTitle>
            <DialogDescription>
              Introduce la URL del sitio web que podrá incrustar el widget de reservas
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="origin">URL del origen</Label>
              <Input
                id="origin"
                value={newOrigin}
                onChange={(e) => setNewOrigin(e.target.value)}
                placeholder="https://ejemplo.com"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddOrigin();
                }}
              />
              <p className="text-xs text-muted-foreground">
                Introduce la URL completa incluyendo el protocolo (https://)
              </p>
            </div>

            <div className="rounded-lg bg-muted p-3">
              <p className="text-xs text-muted-foreground">
                <strong>Ejemplos válidos:</strong>
              </p>
              <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                <li>• https://midominio.com</li>
                <li>• https://www.midominio.com</li>
                <li>• http://localhost:3000 (para desarrollo)</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
              disabled={addMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddOrigin}
              disabled={!newOrigin.trim() || addMutation.isPending}
            >
              {addMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Añadir origen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteOrigin}
        onOpenChange={() => setDeleteOrigin(null)}
        title="¿Eliminar origen?"
        description={`El sitio ${deleteOrigin?.origin} ya no podrá cargar el widget de reservas. Esta acción se puede deshacer añadiendo el origen de nuevo.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
        onConfirm={() => {
          if (deleteOrigin) deleteMutation.mutate(deleteOrigin.id);
        }}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
